import { PageInWP, PageInWPV } from "./types";
import { singularityStore } from "@/store/store";
import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import { pipe } from "fp-ts/lib/function";
import * as t from "io-ts";
import { fold } from "fp-ts/lib/Either";
import { SingularityMeta } from "@/types/document";
import { fpJsonStringify } from "@/util/fp-json-stringify";
import {
  BREAKDANCE_FOOTER_POST_TYPE,
  BREAKDANCE_HEADER_POST_TYPE
} from "@/lib/constants/post-types";
import { Tree } from "@/types/tree";
import { TemplateSettings } from "@/subapps/manage-templates/types";
import { exportTree } from "@/lib/tree/tree";

export async function getPagesFromWPAndPlaceInStore() {
  const pages = await getPagesFromWordPress();
  singularityStore.setPagesInWP(pages);
}

export async function getPagesFromWordPress(): Promise<PageInWP[]> {
  const pages = pipe(
    await makeWordPressAjaxRequest({
      action: "breakdance_singularity_get_pages",
      decoder: t.array(PageInWPV),
      resultKey: "data",
      dataToPost: {
        postType: "page"
      }
    }),
    fold(
      () => [],
      pages => pages
    )
  );

  return pages;
}

export async function deleteAllPagesHeadersAndFootersFromWP() {
  return pipe(
    await makeWordPressAjaxRequest({
      action: "breakdance_singularity_delete_all_pages_headers_and_footers",
      decoder: t.type({ success: t.string })
    }),
    fold(
      () => null,
      _response => {
        singularityStore.setPagesInWP([]);
      }
    )
  );
}

export async function deletePageFromWP(id: number) {
  return pipe(
    await makeWordPressAjaxRequest({
      action: "breakdance_singularity_delete_page",
      decoder: t.type({ success: t.string }),
      dataToPost: { id: id }
    }),
    fold(
      () => null,
      _response => {
        singularityStore.setPagesInWP(
          singularityStore.pagesInWP.filter(page => page.id !== id)
        );
      }
    )
  );
}

export async function addPageInWP(
  title: string,
  singularityMeta: SingularityMeta,
  post_type:
    | "page"
    | typeof BREAKDANCE_HEADER_POST_TYPE
    | typeof BREAKDANCE_FOOTER_POST_TYPE
) {
  return pipe(
    await makeWordPressAjaxRequest({
      action: "breakdance_singularity_add_page",
      decoder: t.type({ success: t.string, id: t.number, relativeUrl: t.string }),
      dataToPost: {
        title: title,
        singularityMeta: fpJsonStringify(singularityMeta),
        post_type: post_type
      }
    }),
    fold(
      () => null,
      response => {
        const newPage: PageInWP = {
          id: response.id,
          relativeUrl: response.relativeUrl,
          title: title,
          postType: post_type,
          singularityMeta: singularityMeta
        };
        singularityStore.setPagesInWP([...singularityStore.pagesInWP, newPage]);
        return newPage;
      }
    )
  );
}

export async function addOrUpdatePageInWP(pageData: {
  title: string;
  postType:
    | "page"
    | typeof BREAKDANCE_HEADER_POST_TYPE
    | typeof BREAKDANCE_FOOTER_POST_TYPE;
  singularityMeta?: SingularityMeta;
  tree?: Tree;
  templateSettings?: TemplateSettings;
  id?: number;
}) {
  return pipe(
    await makeWordPressAjaxRequest({
      action: "breakdance_singularity_add_or_update_page",
      decoder: t.type({ success: t.string, id: t.number, relativeUrl: t.string }),
      dataToPost: {
        id: pageData.id,
        title: pageData.title,
        postType: pageData.postType,
        tree: pageData.tree
          ? fpJsonStringify(exportTree(pageData.tree))
          : undefined,
        singularityMeta: pageData.singularityMeta
          ? fpJsonStringify(pageData.singularityMeta)
          : undefined,
        templateSettings: pageData.templateSettings
          ? fpJsonStringify(pageData.templateSettings)
          : undefined
      }
    }),
    fold(
      () => null,
      response => {
        const newOrUpdatedPage: PageInWP = {
          id: response.id,
          title: pageData.title,
          relativeUrl: response.relativeUrl,
          postType: pageData.postType,
          singularityMeta: {
            ...pageData.singularityMeta
          }
        };

        if (
          !singularityStore.pagesInWP.find(
            page => page.id === newOrUpdatedPage.id
          )
        ) {
          singularityStore.setPagesInWP([
            ...singularityStore.pagesInWP,
            newOrUpdatedPage
          ]);
        }

        return newOrUpdatedPage;
      }
    )
  );
}

export async function updatePageInWP(id: number, title: string) {
  return pipe(
    await makeWordPressAjaxRequest({
      action: "breakdance_singularity_update_page",
      decoder: t.type({ success: t.string }),
      dataToPost: { id: id, title: title }
    }),
    fold(
      () => null,
      _response => {
        const updatedPages = singularityStore.pagesInWP.map(page => {
          if (page.id === id) {
            return { ...page, title: title };
          }
          return page;
        });
        singularityStore.setPagesInWP(updatedPages);
        return true;
      }
    )
  );
}
